/*
 * <bh-address> — the delivery address, checked against the neighbourhood the customer chose.
 *
 * WHY: customers chose a neighbourhood from a list and typed their street much later, in
 * checkout, with nothing linking the two. Some picked the wrong one (#1098: chose Център, lives
 * in Люлин 10 → wrong fee, wrong time slot).
 *
 * HOW: Google suggestions while typing (own listbox, styled like bh-select, Sofia only) are
 * TYPING HELP: picking one puts just the street into the field, cursor ready for the number. The
 * neighbourhood is decided from the FINISHED address: when the customer leaves the field, one
 * Google text search (result accepted only in София) runs on exactly what they typed, and
 * BhZoneMatch maps it to one of our neighbourhoods. If it differs from the one chosen above, the address wins: the select is set
 * the way bh-select sets it (value + bubbling `change`), so the configurator's own listener
 * updates fee, slots and total, and one amber line says so. The customer's text is never
 * replaced: it goes to checkout and to the courier as written.
 *
 * Why the zone does not come from the picked suggestion (that was version 1, 2026-10-11): Google
 * suggests bare streets with no number (no single neighbourhood → false "outside our area"),
 * guesses the wrong "бл. 520" and drops block numbers ("Mladost 4 bl 401" → "Младост 4"). A text
 * search on the full address got all of those right.
 *
 * A number is required (street number or block); without one there is nothing to look up.
 *
 * FAILS OPEN: Google is loaded lazily (Delivery chosen or the field focused; pickup visitors never
 * load it). No key, blocked script, quota, outage → a plain required text field, neighbourhood
 * chosen by hand, exactly as before.
 *
 * WHEN the check runs: on blur, AND 1.2 s after the customer stops typing (a number present), AND
 * at add-to-cart via settle(). Blur alone missed the phone case (2026-10-11): type the address,
 * scroll away without tapping out, the field stays focused and nothing is checked. Add-to-cart
 * awaits settle(); if that last check switches the neighbourhood, the add is stopped so the
 * customer sees the new fee and slot first. A switch always shows a toast with an undo button;
 * undo restores their neighbourhood and the same address never switches it again.
 *
 * The outcome goes on the cake line as `_address_check` (ok / switched a→b / reverted a→b /
 * mismatch / unknown: … / none / skipped), the same way `_addon_check` measures the notes check.
 *
 * Programmatic `input`/`change` events (bh-cart-edit restoring a cake) are ignored: they are not
 * typing and must not trigger a paid lookup.
 */
// Sofia and the outer zones (Бояна, Симеоново, Горубляне, Обеля…). A BIAS for suggestions only;
// results are also filtered to "София". Never use it as a restriction on the text search: that
// makes Google rank worse ("бул. Европа 31" came back in Модерно предградие).
const BH_SOFIA = { west: 23.15, south: 42.58, east: 23.52, north: 42.8 };

class BhAddress extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this.input = this.querySelector('.bh-address__input');
    this.status = this.querySelector('.bh-address__status');
    this.zip = this.querySelector('.bh-address__zip');
    this.result = this.querySelector('.bh-address__result');
    this.select = document.getElementById(this.dataset.zoneSelect);
    if (!this.input || !this.select || !this.zip) return;
    this._built = true;
    window.bhAddress = this; // the configurator awaits settle() before adding to cart
    this.switches = 0;
    this.list = this.querySelector('.bh-address__list');
    this.cache = new Map(); // text → components, so leaving the field twice costs one lookup
    this.items = [];
    this.active = -1;

    // Enabled for Delivery only, so a pickup order never submits an address.
    const setMethod = (method) => {
      this.input.disabled = this.zip.disabled = method !== 'Delivery';
      if (this.result) this.result.disabled = method !== 'Delivery';
      if (method === 'Delivery') this._load();
      else this._close();
    };
    document.getElementById('DeliveryMethodPills')?.addEventListener('click', (e) => {
      const pill = e.target.closest('.bh-delivery-pill');
      if (pill) setMethod(pill.dataset.method);
    });
    setMethod(document.querySelector('input[name="properties[Delivery Method]"]:checked')?.value);

    this.input.addEventListener('focus', () => this._load());
    this.input.addEventListener('input', (e) => {
      if (!e.isTrusted) return;
      this.matched = null;
      this._unknown = '';
      this._stale = null;
      this.zip.value = '';
      this._say('');
      clearTimeout(this._t);
      clearTimeout(this._idle);
      const value = this.input.value;
      // Checked when they pause, not only on blur: on a phone they often scroll away with the
      // field still focused (and the keyboard still up), so blur never comes.
      this._idle = setTimeout(() => {
        if (this.input.value === value && BhZoneMatch.hasNumber(value, this._zones())) this._check();
      }, 1200);
      if (value.trim().length < 3) return this._close();
      this._t = setTimeout(() => this._suggest(value), 250);
    });
    this.input.addEventListener('keydown', (e) => this._onKey(e));
    // On blur, not on `change`: a picked suggestion is set by code, and browsers then fire no
    // change. The cache makes a repeat blur on the same text free.
    this.input.addEventListener('blur', (e) => {
      this._close();
      this.input.value = this.input.value.replace(/[,\s]+$/, ''); // the ", " left after a pick
      if (e.isTrusted) this._check();
    });
    // pointerdown only keeps focus in the field; the pick happens on click. Picking on
    // pointerdown closed the list mid-tap, and the rest of the tap landed on whatever was under
    // it: with the list opened upward, the neighbourhood dropdown (seen live 2026-10-11).
    this.list?.addEventListener('pointerdown', (e) => e.preventDefault());
    this.list?.addEventListener('click', (e) => {
      const opt = e.target.closest('[role="option"]');
      if (opt) this._pick(Number(opt.dataset.index));
    });
    this.select.addEventListener('change', () => { if (!this._setting) this._showZone(); });
    this.undoLabel = this.dataset.tUndo || 'Undo';
  }

  /** Loads Google Maps + Places once per page. Resolves to the places library or null. */
  _load() {
    if (!BhAddress._places) {
      const key = this.dataset.key;
      BhAddress._places = !key ? Promise.resolve(null) : new Promise((resolve, reject) => {
        if (window.google?.maps?.importLibrary) return resolve();
        window.__bhMapsReady = resolve;
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&language=bg&region=BG&loading=async&callback=__bhMapsReady`;
        s.async = true;
        s.onerror = reject;
        document.head.appendChild(s);
      })
        .then(() => window.google.maps.importLibrary('places'))
        .catch(() => null);
    }
    return BhAddress._places;
  }

  async _suggest(value) {
    const places = await this._load();
    if (!places || !this.list) return;
    this.token ||= new places.AutocompleteSessionToken();
    try {
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: value,
        sessionToken: this.token,
        includedRegionCodes: ['bg'],
        // Addresses only: shops came first for "bul. Evropa" and their name went into the field.
        includedPrimaryTypes: ['street_address', 'route', 'premise', 'subpremise', 'neighborhood'],
        locationBias: BH_SOFIA,
        language: 'bg',
        region: 'bg',
      });
      if (this.input.value !== value) return; // a newer keystroke owns the list
      // Sofia only: we deliver nowhere else, and the bias alone still offers Монтана.
      this.items = suggestions.map((s) => s.placePrediction)
        .filter((p) => p && /София|Sofia/.test(p.text.text))
        .slice(0, 5);
      this._render();
    } catch (e) {
      this._close(); // quota / auth / network: the field keeps working as plain text
    }
  }

  _render() {
    this.list.replaceChildren(...this.items.map((p, i) => {
      const opt = document.createElement('div');
      opt.id = `DeliveryAddressOpt${i}`;
      opt.className = 'bh-select__option bh-address__option';
      opt.setAttribute('role', 'option');
      opt.setAttribute('aria-selected', 'false');
      opt.dataset.index = i;
      const main = document.createElement('span');
      main.className = 'bh-address__main';
      main.textContent = p.mainText?.text || p.text.text;
      const sub = document.createElement('span');
      sub.className = 'bh-address__sub';
      sub.textContent = p.secondaryText?.text || '';
      opt.append(main, sub);
      return opt;
    }));
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    const open = this.items.length > 0;
    this.list.hidden = !open;
    this.input.setAttribute('aria-expanded', String(open));
    // On a phone the space below is taken by the keyboard and the sticky add-to-cart bar, so
    // open upwards when it does not fit. (No scrolling: it fights iOS's own keyboard scroll.)
    if (open) {
      this.list.classList.remove('bh-address__list--up');
      const viewport = window.visualViewport?.height || window.innerHeight;
      const box = this.input.getBoundingClientRect();
      const need = this.list.offsetHeight + 90; // 90 ≈ the sticky add-to-cart bar
      if (box.bottom + need > viewport && box.top > need) this.list.classList.add('bh-address__list--up');
    }
  }

  _close() {
    if (!this.list) return;
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  _onKey(e) {
    if (e.key === 'Enter' && (this.list?.hidden !== false || this.active < 0)) {
      e.preventDefault(); // never submit the product form from here
      this._close();
      this.input.blur();
      return;
    }
    if (!this.list || this.list.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const n = this.items.length;
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
      this.list.querySelectorAll('[role="option"]').forEach((o, i) => {
        o.classList.toggle('is-active', i === this.active);
        o.setAttribute('aria-selected', String(i === this.active));
      });
      this.input.setAttribute('aria-activedescendant', `DeliveryAddressOpt${this.active}`);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      this._pick(this.active);
    } else if (e.key === 'Escape') {
      this._close();
    }
  }

  /**
   * Typing help only: the street goes into the field and the cursor waits for the number. If the
   * suggestion already carries the customer's number, they can carry on with entrance and flat.
   */
  _pick(i) {
    const p = this.items[i];
    if (!p) return;
    this._close();
    this.token = null; // a new search starts a new Google session
    const typed = this.input.value;
    const street = p.mainText?.text || p.text.text;
    // Never lose what the customer typed: "Mladost 4 bl 401" must not become "Младост 4".
    const lost = (typed.match(/\d+/g) || []).some((n) => !street.includes(n));
    if (lost) { this.input.focus(); return; }
    this.input.value = `${street}${BhZoneMatch.hasNumber(street, this._zones()) ? ', ' : ' '}`;
    this.input.focus();
    const end = this.input.value.length;
    this.input.setSelectionRange(end, end);
  }

  _check() {
    const text = this.input.value.trim();
    if (!text) return null;
    if (text === this._checked) {
      // Same text again (retyped, or blur after the pause check): reuse the answer, no new lookup.
      if (!this._pending && !this.matched && this.cache.has(text)) this._apply(text, this.cache.get(text));
      return this._pending;
    }
    this._checked = text;
    this._pending = this._lookup(text).finally(() => { if (this._checked === text) this._pending = null; });
    return this._pending;
  }

  /**
   * Called by the configurator before adding to cart. Runs the check if the latest text was never
   * checked (still focused, never paused), waits for it (max 2.5 s, then fails open) and answers
   * true when it just switched the neighbourhood, so the add stops and the customer sees why.
   */
  async settle() {
    if (this.input.disabled) return false;
    const text = this.input.value.trim();
    if (!text || !BhZoneMatch.hasNumber(text, this._zones())) return false;
    const before = this.switches;
    const pending = this._check();
    if (pending) {
      const done = await Promise.race([pending.then(() => true), new Promise((r) => setTimeout(() => r(false), 2500))]);
      // Gave up: the cake goes in with the neighbourhood as chosen, so a late answer must not
      // switch the page (and toast) behind a cake that is already in the cart.
      if (!done) this._stale = text;
    }
    this._record();
    return this.switches !== before;
  }

  async _lookup(text) {
    const zones = this._zones();
    if (!BhZoneMatch.hasNumber(text, zones)) return this._say(this.dataset.tNumber, 'warn');
    let components = this.cache.get(text);
    if (!components) {
      const places = await this._load();
      if (!places) return;
      try {
        // No locationRestriction: it makes Google rank worse ("бул. Европа 31" came back as a
        // street in Модерно предградие). Search plainly, then accept only a result in София.
        const { places: found } = await places.Place.searchByText({
          textQuery: /софия|sofia/i.test(text) ? text : `${text}, София`,
          fields: ['addressComponents'],
          language: 'bg',
          region: 'bg',
          maxResultCount: 1,
        });
        const c = found?.[0]?.addressComponents || [];
        const inSofia = c.some((x) => (x.types || []).includes('locality') && /^(София|Sofia)$/i.test(x.longText || ''));
        components = inSofia ? c : [];
      } catch (e) {
        return; // quota / auth / network: the field keeps working as plain text
      }
      this.cache.set(text, components);
    }
    if (this.input.value.trim() !== text) return; // edited while we waited
    this._apply(text, components);
  }

  /** Our neighbourhoods, as offered in the select (the only ones we deliver to). */
  _zones() {
    const handles = new Set(Array.from(this.select.options).map((o) => o.value).filter(Boolean));
    return (window.BakehuntersConfig?.zones || []).filter((z) => handles.has(z.handle));
  }

  _apply(text, components) {
    if (this._stale === text) return this._record();
    this._looked = text; // Google answered for this text (vs. skipped: no key, outage, timeout)
    const zones = this._zones();
    const r = BhZoneMatch.matchZone(components, zones);
    // A block address ("бл. 520") has no street, and Google's guess at which block is weak in
    // Sofia. There, a neighbourhood the customer typed ("Lyulin 5 bl 520") beats Google's;
    // Google's disagreement then shows as the amber "провери" note. With a real street, Google
    // wins. Google files "бл. 520" as a route too, so a block-named route is not a street.
    const hasStreet = components.some((c) => (c.types || []).includes('route') && !/^(бл|блок)(\.|\s|$)/i.test(c.longText || ''));
    const typed = hasStreet ? null : BhZoneMatch.typedZone(text, zones);
    const target = typed || r.handle;
    // Postcode for the checkout prefill, unless Google's place is not the customer's.
    const zip = components.find((c) => (c.types || []).includes('postal_code'))?.longText || '';
    this.zip.value = typed && r.handle && typed !== r.handle ? '' : zip;
    this.matched = r.handle || typed;
    if (!target) {
      // No match and no named area says nothing: absence of a match is not evidence the address
      // is outside our area. Only a NAMED other area is.
      this._unknown = r.googleName || '';
      if (r.googleName) this._say(this.dataset.tUnknown.replace('{place}', r.googleName), 'warn');
      return this._record();
    }
    // The neighbourhood above is usually already chosen. The address wins; say so, because the
    // fee and the time slot may have just changed.
    const before = this.select.value;
    // They undid a switch for this exact address: their neighbourhood stands (border streets,
    // #1091 ул. Тракия: Google says Център, the customer knows it is Оборище).
    if (this._kept === text) { this._showZone(); return this._record(); }
    this.switched = before && before !== target;
    if (before !== target) {
      const slot = this._slot();
      this._setZone(target);
      this.switches += 1;
      this.from = before;
      const after = this._slot();
      this.slotNote = slot.value && after.value !== slot.value ? after.label : '';
      if (before) this._toast(before, target, text);
    }
    this._showZone();
    this._record();
  }

  _setZone(handle) {
    this._setting = true;
    this.select.value = handle;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this._setting = false;
  }

  /** The chosen delivery slot, read from the configurator's select. */
  _slot() {
    const s = document.getElementById('TimeSlotSelect');
    const o = s?.selectedOptions?.[0];
    return { value: s?.value || '', label: (o?.textContent || '').trim() };
  }

  _zoneLabel(handle) {
    const o = this.select.querySelector(`option[value="${CSS.escape(handle)}"]`);
    return (o?.textContent || '').replace(/\s*\(.*\)$/, '').trim();
  }

  /**
   * "Сменихме квартала на Люлин 10 според адреса. Часът вече е 20:00 - 22:00." with an undo
   * button. role=status, not alert: it is news, not an error, and the button must not steal focus.
   */
  _toast(from, to, text) {
    let t = BhAddress._toastEl;
    if (!t) {
      t = BhAddress._toastEl = document.createElement('div');
      t.className = 'bh-address-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      t.innerHTML = '<span class="bh-address-toast__text"></span><button type="button" class="bh-address-toast__undo"></button>';
      document.body.appendChild(t);
    }
    const msg = this.dataset.tToast.replace('{zone}', this._zoneLabel(to))
      + (this.slotNote ? ` ${this.dataset.tToastSlot.replace('{slot}', this.slotNote)}` : '');
    t.querySelector('.bh-address-toast__text').textContent = msg;
    const undo = t.querySelector('.bh-address-toast__undo');
    undo.textContent = this.undoLabel.replace('{zone}', this._zoneLabel(from));
    undo.onclick = () => { this._undo(); hide(); };
    const hide = () => { t.classList.remove('bh-address-toast--show'); clearTimeout(BhAddress._toastTimer); };
    t.classList.remove('bh-address-toast--show');
    void t.offsetWidth;
    t.classList.add('bh-address-toast--show');
    clearTimeout(BhAddress._toastTimer);
    BhAddress._toastTimer = setTimeout(hide, 8000); // long enough to read and reach the button
  }

  /** Back to the neighbourhood the customer chose; this address will not switch it again. */
  _undo() {
    if (!this.from || !this.switched) return;
    const to = this.select.value;
    this._kept = this.input.value.trim();
    this._setZone(this.from);
    this.switched = false;
    this.reverted = `${to}→${this.from}`;
    if (BhAddress._toastEl) BhAddress._toastEl.classList.remove('bh-address-toast--show');
    this._showZone();
    this._record();
  }

  /** `_address_check` on the cake line: what the check concluded, for measuring it. */
  _record() {
    if (!this.result) return;
    const sel = this.select.value;
    let v = 'skipped';
    if (this.matched && sel === this.matched) v = this.switched && this.from ? `switched ${this.from}→${sel}` : 'ok';
    else if (this.matched && this.reverted && this._kept === this.input.value.trim()) v = `reverted ${this.reverted}`;
    else if (this.matched) v = `mismatch google=${this.matched}`;
    else if (this._looked === this.input.value.trim() && this._unknown) v = `unknown: ${this._unknown}`;
    else if (this._looked === this.input.value.trim()) v = 'none';
    this.result.value = v;
  }

  /** The line under the field: only a switch or a conflict is worth saying. */
  _showZone() {
    if (!this.matched) return;
    const label = this.select.querySelector(`option[value="${CSS.escape(this.matched)}"]`)?.textContent || '';
    if (this.select.value === this.matched) {
      const slot = this.switched && this.slotNote ? ` ${this.dataset.tToastSlot.replace('{slot}', this.slotNote)}` : '';
      this._say(this.switched ? this.dataset.tSwitched + slot : '', 'warn');
      if (this.switched && this.from) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bh-address__undo';
        b.textContent = this.undoLabel.replace('{zone}', this._zoneLabel(this.from));
        b.addEventListener('click', () => this._undo());
        this.status.append(' ', b);
      }
    }
    else this._say(this.dataset.tMismatch.replace('{zone}', label.replace(/\s*\(.*\)$/, '')), 'warn');
  }

  _say(text, tone) {
    this.status.textContent = text;
    this.status.hidden = !text;
    if (tone) this.status.dataset.tone = tone;
  }
}

if (!customElements.get('bh-address')) customElements.define('bh-address', BhAddress);
